import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  EmployeePay,
  HistoryVoidReportLine,
  MasterPayrollObject,
  Payline,
  PaylineType,
  PayTypes,
  ThirdPartyPayee,
  ThirdPartyReportLine
} from '../../../../models/payroll';
import { Observable, forkJoin } from 'rxjs';
import { ApiResponse } from '../../../../models/api-response';
import { PayrollManagement } from '../../../../tools/payroll-management';
import { PayrollService } from '../../../../services/payroll.service';
import { concatMap, tap } from 'rxjs/operators';
import { CompaniesService } from '../../../../services/companies.service';
import { CompanyDeductionConfig } from '../../../../models/company-deduction-config';
import { BasePayrollComponent } from '../../../../components/base/base-payroll.component';

@Component({
  selector: 'lib-history-void-report',
  templateUrl: './history-void-report.component.html',
  styleUrls: ['./history-void-report.component.scss'],
  standalone: false
})
export class HistoryVoidReportComponent extends BasePayrollComponent implements OnInit {
  @Input() public clientCode: string;
  @Input() public deductionConfigs: CompanyDeductionConfig[];
  @Input() public payroll: MasterPayrollObject;
  @Output() showHistoryVoidMessage: EventEmitter<boolean> = new EventEmitter();

  public employeePaysHistory: EmployeePay[];
  public historyVoids: HistoryVoidReportLine[];
  public reportTitle: string;
  public thirdPartyReport: ThirdPartyReportLine[] = [];

  constructor(
    private companyService: CompaniesService,
    private payrollMgmt: PayrollManagement,
    private payrollService: PayrollService
  ) {
    super();
  }

  ngOnInit(): void {
    this._setReportTitle();

    if (
      this.payroll.PayType == PayTypes.Adjustment &&
      this._withinPast30Days(this.payroll.PayDate)
    ) {
      this._buildHistoryVoidReportForCorrections(this.payroll.EmployeePays);
    } else {
      this._buildHistoryVoidReport(this.payroll.EmployeePays);
    }
  }

  private _buildHistoryVoidReportLines(pay: EmployeePay, payline: Payline) {
    const reportLines = [];

    const reportLine = new HistoryVoidReportLine();

    reportLine.EmployeeId = pay.EmployeeNumber;

    reportLine.PayrollSeq = payline.HistoryVoidData.PayrollSeq;
    reportLine.PayrollYear = payline.HistoryVoidData.PayrollYear;
    reportLine.PaylineId = payline.HistoryVoidData.PaylineId;
    reportLine.CheckNo = this.checkNumberDisplay(payline.HistoryVoidData.CheckNumber);

    reportLine.EmployeeName = this.payrollMgmt.getEmployeeFullName(pay);

    if (payline.NetAmountDeposited && payline.NetCheckAmountPaid) {
      // if the payment method was split, report it as two lines here
      reportLine.Method = 'ACH';
      reportLine.Amount = Math.abs(payline.NetAmountDeposited);
      reportLines.push(reportLine);

      const reportLineSplit = { ...reportLine };
      reportLineSplit.Method = 'Check';
      reportLineSplit.Amount = Math.abs(payline.NetCheckAmountPaid);
      reportLines.push(reportLineSplit);
    } else {
      reportLine.Method = !payline.NetAmountDeposited ? 'Check' : 'ACH';
      reportLine.Amount = Math.abs(payline.NetAmountPaid);
      reportLines.push(reportLine);
    }

    return reportLines;
  }

  private _buildReportLinesForCorrections(pay: EmployeePay, payline: Payline) {
    // find the employee pays history data that has
    // OverrideCheckNumber, NetAmountDeposited, NetCheckAmountPaid
    const lines = this.employeePaysHistory.find(
      x => x.EmployeeNumber == pay.EmployeeNumber
    )?.Paylines;
    const line = lines?.find(x => x.NetAmountPaid == Math.abs(payline.NetAmountPaid));

    const reportLines = [];

    if (!line) return reportLines;

    const reportLine = new HistoryVoidReportLine();
    reportLine.EmployeeId = pay.EmployeeNumber;
    reportLine.PayrollSeq = this.payroll.SequenceNumber;
    reportLine.PayrollYear = this.payroll.Year;
    reportLine.PaylineId = payline.PaylineId;
    reportLine.PayDate = this.payroll.PayDate;
    reportLine.CheckNo = this.checkNumberDisplay(line?.OverrideCheckNumber);
    reportLine.EmployeeName = this.payrollMgmt.getEmployeeFullName(pay);

    if (line.NetAmountDeposited && line.NetCheckAmountPaid) {
      // if the payment method was split, report it as two lines here
      reportLine.Method = 'ACH';
      reportLine.Amount = Math.abs(line.NetAmountDeposited);
      reportLines.push(reportLine);

      const reportLineSplit = { ...reportLine };
      reportLineSplit.Method = 'Check';
      reportLineSplit.Amount = Math.abs(line.NetCheckAmountPaid);
      reportLines.push(reportLineSplit);
    } else {
      reportLine.Method = !line.NetAmountDeposited ? 'Check' : 'ACH';
      reportLine.Amount = Math.abs(line.NetAmountPaid);
      reportLines.push(reportLine);
    }

    return reportLines;
  }

  // gather a list of history void paylines
  // fetch completed payrolls for each unique year/seq from HistoryVoidData
  // filter out any history voids that have an original pay date > 30 days
  private _buildHistoryVoidReport(pays: EmployeePay[]) {
    let historyVoids: HistoryVoidReportLine[] = [];
    const payrolls: Record<number, number[]> = {};

    for (let pay of pays) {
      for (let payline of pay.Paylines) {
        if (!payline.OkToPay) {
          continue;
        }

        if (payline.PaylineType == PaylineType.Void) {
          const lines = this._buildHistoryVoidReportLines(pay, payline);

          historyVoids.push(...lines);

          // for each payline being voided
          // grab payroll year/seq in order to fetch completed payroll data
          // to get the original pay dates (for greater than 30 day check)
          if (!payrolls[lines[0].PayrollYear]) {
            payrolls[lines[0].PayrollYear] = [];
          }

          if (!payrolls[lines[0].PayrollYear].includes(lines[0].PayrollSeq)) {
            payrolls[lines[0].PayrollYear].push(lines[0].PayrollSeq);
          }
        }
      }
    }

    if (!Object.keys(payrolls).length) {
      return;
    }

    const completedPayrolls: Observable<ApiResponse<MasterPayrollObject>>[] = [];

    Object.keys(payrolls).forEach(year => {
      const sequenceNumbers = payrolls[year];
      for (let seq of sequenceNumbers) {
        completedPayrolls.push(
          this.payrollService.getCompletedPayroll(this.clientCode, year, seq).pipe(
            tap(resp => {
              historyVoids
                .filter(x => x.PayrollYear == +year && x.PayrollSeq == seq)
                .map(x => (x.PayDate = resp.Model.PayDate));
            })
          )
        );
      }
    });

    forkJoin(completedPayrolls).subscribe(() => {
      historyVoids = historyVoids.filter(x => {
        return this._withinPast30Days(x.PayDate);
      });

      this.showHistoryVoidMessage.emit(historyVoids.length > 0);
      this.historyVoids = historyVoids;

      this.companyService.getThirdPartyPayees(this.clientCode).subscribe(thirdPartyPayees => {
        this._buildThirdPartyReport(thirdPartyPayees);
      });
    });
  }

  // workflow to show history void report on the correction payroll preview screen
  private _buildHistoryVoidReportForCorrections(pays: EmployeePay[]) {
    this.payrollService
      .getPayrollInfo(this.clientCode, this.payroll.Year, this.payroll.SequenceNumber)
      .pipe(
        concatMap(pri => {
          return this.payrollService.getEmployeePaysHistory(
            this.clientCode,
            this.payroll.Year.toString(),
            pri.CorrectionSequence
          );
        })
      )
      .subscribe(emp => {
        this.employeePaysHistory = emp;
        let historyVoids: HistoryVoidReportLine[] = [];
        for (let pay of pays) {
          for (let payline of pay.Paylines) {
            if (!payline.OkToPay) {
              continue;
            }

            const lines = this._buildReportLinesForCorrections(pay, payline);
            historyVoids.push(...lines);
          }
        }

        this.showHistoryVoidMessage.emit(historyVoids.length > 0);
        this.historyVoids = historyVoids;

        this.companyService.getThirdPartyPayees(this.clientCode).subscribe(thirdPartyPayees => {
          this._setThirdPartyReport(
            emp,
            thirdPartyPayees,
            this.payroll.Year,
            this.payroll.SequenceNumber
          );
        });
      });
  }

  // for each unique payroll year/seq being voided and within the 30 day range
  // fetch EmployeePaysHistory in order to build thirdPartyReport
  private _buildThirdPartyReport(thirdPartyPayees: ThirdPartyPayee[]) {
    const payrolls: Record<number, number[]> = {};

    if (!this.historyVoids.length) {
      return;
    }

    for (let v of this.historyVoids) {
      if (!payrolls[v.PayrollYear]) {
        payrolls[v.PayrollYear] = [];
      }

      if (!payrolls[v.PayrollYear].includes(v.PayrollSeq)) {
        payrolls[v.PayrollYear].push(v.PayrollSeq);
      }
    }

    const completedPayrolls: Observable<EmployeePay[]>[] = [];

    Object.keys(payrolls).forEach(year => {
      const sequenceNumbers = payrolls[year];
      for (let seq of sequenceNumbers) {
        completedPayrolls.push(
          this.payrollService.getEmployeePaysHistory(this.clientCode, year, seq).pipe(
            tap(emps => {
              this.employeePaysHistory = emps;
              this._setThirdPartyReport(emps, thirdPartyPayees, year, seq);
            })
          )
        );
      }
    });

    forkJoin(completedPayrolls).subscribe();
  }

  private _setReportTitle() {
    this.companyService
      .getClientSetup(this.clientCode)
      .pipe(
        concatMap(setup => {
          this.reportTitle = setup.HasCentralChecking ? 'Central Checking' : 'Client Check';

          return this.companyService.getLocations(this.clientCode).pipe(
            tap(location => {
              this.reportTitle = `${this.reportTitle}${
                location[0].AllowDirectDeposit ? ' & Direct Deposit' : ''
              }`;
            })
          );
        })
      )
      .subscribe(() => {});
  }

  private _setThirdPartyReport(
    emps: EmployeePay[],
    thirdPartyPayees: ThirdPartyPayee[],
    year,
    seq
  ) {
    for (let emp of emps) {
      for (let payline of emp.Paylines) {
        // try to map back to an entry in this.historyVoidData
        const historyVoid = this.historyVoids.find(
          x =>
            x.PayrollYear == +year &&
            x.PayrollSeq == seq &&
            x.EmployeeId == emp.EmployeeNumber &&
            x.PaylineId == payline.PaylineId
        );

        if (!historyVoid) {
          continue;
        }

        // loop through all of the deductions from this history endpoint
        // if an associated record is found in the third party payee config
        // push a new item into thirdPartyReport
        for (let ded of payline.Deductions) {
          // find config and check if CompanyDeductionConfig.ThirdPartyPayee != 0
          const config = this.deductionConfigs.find(x => x.Id == ded.DeductionCode);
          if (!config?.ThirdPartyPayee) {
            continue;
          }

          const t = thirdPartyPayees.find(x => x.Id == ded.ThirdPartyPayeeId);

          if (!t || !ded.CurrentAmount || ded.HoldThirdPartyDisb) {
            continue;
          }

          const thirdPartyReportLine = new ThirdPartyReportLine(historyVoid);
          thirdPartyReportLine.Amount = Math.abs(ded.CurrentAmount);
          thirdPartyReportLine.Payee = t.Name;
          thirdPartyReportLine.Deduction = config.Name;
          thirdPartyReportLine.Method = t.PaymentMethod == 1 ? 'Check' : 'ACH';
          thirdPartyReportLine.Combined = ded.IsCheckCombined ? 'Yes' : 'No';
          this.thirdPartyReport.push(thirdPartyReportLine);
        }
      }
    }
  }

  private _withinPast30Days(date: Date) {
    let day = new Date();
    day.setDate(day.getDate() - 30);
    const thirtyDaysAgo = new Date(day.getFullYear(), day.getMonth(), day.getDate());

    return new Date(date) >= thirtyDaysAgo;
  }
}
